/**
 * Route table.
 *
 * - Auth-public routes (`/`, `/oauth/callback`) load eagerly so the
 *   cold-start landing page has zero JS round trips.
 * - Everything under `/app` is wrapped by `AppShellLayout` (sidebar +
 *   topbar + outlet) and guarded by `ProtectedRoute`. Each nested
 *   route renders inside the shell's outlet, so adding a new page is
 *   just a `{ path, element }` entry.
 * - `/app/<unbuilt-section>` resolves to `ComingSoonPage` until each
 *   feature ships its real route.
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, createBrowserRouter, RouterProvider } from 'react-router';

import { Loader } from '@/components/ui/loader/loader';
import { paths } from '@/config/paths';
import { ProtectedRoute } from '@/features/auth/components/protected-route';
import { GetStartedPage } from '@/features/auth/routes/get-started';
import { OAuthCallbackPage } from '@/features/auth/routes/oauth-callback';
import { SubscriptionGate } from '@/features/billing/components/subscription-gate';
import { OnboardingGate } from '@/features/onboarding/components/onboarding-gate';

import { AppShellRoute } from './routes/app-shell-route';

const DashboardPage = lazy(() => import('@/features/dashboard/routes/dashboard'));
const AvatarsPage = lazy(() => import('@/features/avatars/routes/avatars-page'));
const AvatarStartPage = lazy(() => import('@/features/avatars/routes/avatar-start'));
const TemplateBrowserPage = lazy(() => import('@/features/avatars/routes/template-browser'));
const AvatarWizardPage = lazy(() => import('@/features/avatars/routes/avatar-wizard'));
const AvatarNeedsVoicePage = lazy(() => import('@/features/avatars/routes/avatar-needs-voice'));
const AvatarDetailPage = lazy(() => import('@/features/avatars/routes/avatar-detail'));
const PostsPage = lazy(() => import('@/features/posts/routes/posts'));
const FollowedPage = lazy(() => import('@/features/followed/routes/followed'));
const ReelAnalyticsPage = lazy(() => import('@/features/reel-analytics/routes/reel-analytics'));
const FollowAccountDetailsPage = lazy(() => import('@/features/analytics/routes/analytics'));
const AutomationPage = lazy(() => import('@/features/automation/routes/automation'));
const CampaignPage = lazy(() => import('@/features/campaign/routes/campaign'));
const SettingsPage = lazy(() => import('@/features/settings/routes/settings'));
const BillingPage = lazy(() => import('@/features/billing/routes/billing'));
const HelpPage = lazy(() => import('@/features/help/routes/help'));
const VideosPage = lazy(() => import('@/features/videos/routes/videos-page'));
const VideoDetailPage = lazy(() => import('@/features/videos/routes/video-detail'));
const VideoQaPage = lazy(() => import('@/features/videos/routes/video-qa'));
const CopyReelPage = lazy(() => import('@/features/copy-reel/routes/copy-reel'));
const SceneGeneratorPage = lazy(() => import('@/features/scenes/routes/scene-generator'));
const VideoWizardPage = lazy(() => import('@/features/generate/routes/video-wizard'));
const ComingSoonPage = lazy(() => import('./routes/coming-soon'));

// Public auth + signup funnel (Wave 4). Lazy so the cold-start landing
// page (`/`, eager) stays minimal; these load on first navigation.
const SignupPage = lazy(() => import('@/features/auth/routes/signup'));
const LoginPage = lazy(() => import('@/features/auth/routes/login'));
const PackagesPage = lazy(() => import('@/features/billing/routes/packages'));
const CheckoutPage = lazy(() => import('@/features/billing/routes/checkout'));
const OnboardingExperiencePage = lazy(
  () => import('@/features/onboarding/routes/onboarding-experience'),
);
const OnboardingSourcePage = lazy(() => import('@/features/onboarding/routes/onboarding-source'));

/**
 * Shared fallback shown while a lazy route chunk (and its first data
 * load via Suspense) resolves. The branded orb is the single loading
 * mark for the whole app - see `components/ui/loader`.
 */
const routeFallback = <Loader fullScreen size="lg" data-testid="route-loader" />;

const stub = <Suspense fallback={routeFallback}>{<ComingSoonPage />}</Suspense>;

/** Wraps a lazy route element in the shared Suspense boundary. */
function lazyRoute(element: ReactNode) {
  return <Suspense fallback={routeFallback}>{element}</Suspense>;
}

// Dev-only routes (under `/app/_dev/*`). Gated by `import.meta.env.DEV`
// so the lazy import is unreachable in production and Rollup drops the
// chunk. The route table itself is built from a `let` array so the
// conditional `if (DEV)` push is statically eliminated by the bundler,
// keeping the page out of `dist/`. Same tree-shake pattern as the
// `_dev/screen-review-widget` barrel.
const devAppRoutes: { path: string; element: ReactNode }[] = [];
if (import.meta.env.DEV) {
  const WizardReviewIndexPage = lazy(() => import('@/features/_dev/wizard-review-index'));
  devAppRoutes.push({
    path: '_dev/wizard-review',
    element: lazyRoute(<WizardReviewIndexPage />),
  });
}

const router = createBrowserRouter([
  { path: paths.home, element: <GetStartedPage /> },
  { path: paths.oauthCallback, element: <OAuthCallbackPage /> },
  { path: paths.signup, element: lazyRoute(<SignupPage />) },
  { path: paths.login, element: lazyRoute(<LoginPage />) },
  { path: paths.packages, element: lazyRoute(<PackagesPage />) },
  { path: paths.checkout, element: lazyRoute(<CheckoutPage />) },
  {
    path: paths.onboardingExperience,
    element: lazyRoute(<OnboardingExperiencePage />),
  },
  {
    path: paths.onboardingSource,
    element: lazyRoute(<OnboardingSourcePage />),
  },
  {
    path: paths.app,
    element: (
      <ProtectedRoute>
        <SubscriptionGate>
          <OnboardingGate>
            <AppShellRoute />
          </OnboardingGate>
        </SubscriptionGate>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to={paths.dashboard} replace /> },
      ...devAppRoutes,
      {
        // Get Started now lives at the top of Home (2026-06-30); keep the
        // path as a redirect so deep links and the sidebar entry land there.
        path: 'getting-started',
        element: <Navigate to={paths.dashboard} replace />,
      },
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={routeFallback}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'avatars',
        element: (
          <Suspense fallback={routeFallback}>
            <AvatarsPage />
          </Suspense>
        ),
      },
      {
        // The avatar-creation entry screen ("How do you want to build
        // it?" — design 03b): a path chooser between "Start from
        // scratch" and the featured/all templates.
        path: 'avatars/new',
        element: (
          <Suspense fallback={routeFallback}>
            <AvatarStartPage />
          </Suspense>
        ),
      },
      {
        // The full niche-filtered template browser (design 03a),
        // reached from the entry screen's "See all 68 templates" CTA.
        // Static `new/templates` must precede the dynamic `:id`.
        path: 'avatars/new/templates',
        element: (
          <Suspense fallback={routeFallback}>
            <TemplateBrowserPage />
          </Suspense>
        ),
      },
      {
        // Static `new/wizard` must precede the dynamic `:id` so React
        // Router matches the literal segment first.
        path: 'avatars/new/wizard',
        element: (
          <Suspense fallback={routeFallback}>
            <AvatarWizardPage />
          </Suspense>
        ),
      },
      {
        // Voice gate · shown when a voiceless avatar is picked for a
        // video. Deeper than `avatars/:id`, so it matches unambiguously.
        path: 'avatars/:id/needs-voice',
        element: (
          <Suspense fallback={routeFallback}>
            <AvatarNeedsVoicePage />
          </Suspense>
        ),
      },
      {
        path: 'avatars/:id',
        element: (
          <Suspense fallback={routeFallback}>
            <AvatarDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'videos',
        element: (
          <Suspense fallback={routeFallback}>
            <VideosPage />
          </Suspense>
        ),
      },
      {
        // `videos/:id` and `videos/:id/qa` are the two children of the
        // `videos` group; both are dynamic so order is not load-bearing.
        path: 'videos/:id',
        element: (
          <Suspense fallback={routeFallback}>
            <VideoDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'videos/:id/qa',
        element: (
          <Suspense fallback={routeFallback}>
            <VideoQaPage />
          </Suspense>
        ),
      },
      {
        path: 'generate',
        element: (
          <Suspense fallback={routeFallback}>
            <VideoWizardPage />
          </Suspense>
        ),
      },
      {
        path: 'copy-reel',
        element: (
          <Suspense fallback={routeFallback}>
            <CopyReelPage />
          </Suspense>
        ),
      },
      {
        path: 'scenes/new',
        element: (
          <Suspense fallback={routeFallback}>
            <SceneGeneratorPage />
          </Suspense>
        ),
      },
      {
        path: 'followed',
        element: (
          <Suspense fallback={routeFallback}>
            <FollowedPage />
          </Suspense>
        ),
      },
      {
        path: 'reel-analytics',
        element: (
          <Suspense fallback={routeFallback}>
            <ReelAnalyticsPage />
          </Suspense>
        ),
      },
      {
        path: 'follow-account-details/:id',
        element: (
          <Suspense fallback={routeFallback}>
            <FollowAccountDetailsPage />
          </Suspense>
        ),
      },
      {
        path: 'posts',
        element: (
          <Suspense fallback={routeFallback}>
            <PostsPage />
          </Suspense>
        ),
      },
      {
        path: 'automation',
        element: (
          <Suspense fallback={routeFallback}>
            <AutomationPage />
          </Suspense>
        ),
      },
      {
        path: 'campaign',
        element: (
          <Suspense fallback={routeFallback}>
            <CampaignPage />
          </Suspense>
        ),
      },
      { path: 'ideation', element: stub },
      {
        path: 'settings',
        element: (
          <Suspense fallback={routeFallback}>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: 'billing',
        element: (
          <Suspense fallback={routeFallback}>
            <BillingPage />
          </Suspense>
        ),
      },
      {
        path: 'help',
        element: (
          <Suspense fallback={routeFallback}>
            <HelpPage />
          </Suspense>
        ),
      },
      { path: '*', element: <Navigate to={paths.dashboard} replace /> },
    ],
  },
  { path: '*', element: <GetStartedPage /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
